import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useLanguage } from '../i18n/LanguageContext';
import Navbar from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { 
  Eye,
  Leaf,
  TreeDeciduous,
  Globe,
  Shield,
  Sparkles,
  ArrowRight,
  Target,
  Flame,
  RefreshCw
} from 'lucide-react';

export default function VisionPage() {
  const { t } = useLanguage();

  return (
    <div className="min-h-screen bg-background" data-testid="vision-page">
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative pt-16 pb-24 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-background to-background" />
        </div>
        
        <div className="relative max-w-4xl mx-auto px-4 pt-16">
          <motion.div 
            className="text-center"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="eco-badge mb-4 mx-auto w-fit">
              <Eye className="w-3.5 h-3.5" />
              {t('vision.badge')}
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-foreground mb-6">
              {t('vision.title')}
            </h1>
          </motion.div>
        </div>
      </section>

      {/* Main Vision Content */}
      <section className="py-16 px-4 bg-card">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="space-y-8"
          >
            {/* Opening Statement */}
            <Card className="natural-card p-8 border-l-4 border-primary">
              <p className="text-xl text-foreground leading-relaxed font-medium">
                {t('vision.opening')}
              </p>
            </Card>

            {/* What We Are */}
            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <Target className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-foreground mb-3">{t('vision.whatWeAre.title')}</h2>
                  <p className="text-muted-foreground leading-relaxed">
                    {t('vision.whatWeAre.description')}
                  </p>
                </div>
              </div>
            </div>

            {/* Blockchain Transparency */}
            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-lg bg-blue-500/10 flex items-center justify-center flex-shrink-0">
                  <Shield className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-foreground mb-3">{t('vision.transparency.title')}</h2>
                  <p className="text-muted-foreground leading-relaxed">
                    {t('vision.transparency.description')}
                  </p>
                </div>
              </div>
            </div>

            {/* Our Belief */}
            <Card className="natural-card p-8 bg-gradient-to-br from-primary/5 to-accent/5">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-lg bg-accent/10 flex items-center justify-center flex-shrink-0">
                  <Sparkles className="w-6 h-6 text-accent" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-foreground mb-3">{t('vision.belief.title')}</h2>
                  <p className="text-foreground leading-relaxed">
                    {t('vision.belief.description')}
                  </p>
                </div>
              </div>
            </Card>

            {/* Our Goal */}
            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-lg bg-green-500/10 flex items-center justify-center flex-shrink-0">
                  <Globe className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-foreground mb-3">{t('vision.goal.title')}</h2>
                  <p className="text-muted-foreground leading-relaxed">
                    {t('vision.goal.description')}
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Closing Statement */}
      <section className="py-20 px-4 eco-gradient text-white">
        <div className="max-w-3xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="flex justify-center gap-4 mb-8">
              <div className="w-16 h-16 rounded-full bg-white/10 flex items-center justify-center">
                <Flame className="w-8 h-8" />
              </div>
              <div className="w-16 h-16 rounded-full bg-white/10 flex items-center justify-center">
                <ArrowRight className="w-8 h-8" />
              </div>
              <div className="w-16 h-16 rounded-full bg-white/10 flex items-center justify-center">
                <RefreshCw className="w-8 h-8" />
              </div>
            </div>
            
            <p className="text-2xl font-light mb-4 text-white/90">
              {t('vision.closing.line1')}
            </p>
            <p className="text-2xl font-light mb-12 text-white/90">
              {t('vision.closing.line2')}
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button asChild size="lg" className="bg-white text-primary hover:bg-white/90">
                <Link to="/adopt">
                  <TreeDeciduous className="w-5 h-5 mr-2" />
                  {t('landing.heroCta')}
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="border-white/30 text-white hover:bg-white/10">
                <Link to="/transparency">
                  {t('nav.transparency')}
                </Link>
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 bg-background border-t border-border">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                <Leaf className="w-5 h-5 text-primary" />
              </div>
              <span className="font-semibold text-lg text-foreground">Epuyén Renace</span>
            </div>
            <p className="text-sm text-muted-foreground">
              © {new Date().getFullYear()} Epuyén Renace. {t('landing.footerTagline')}
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
